import React, { useCallback, useMemo, useState } from "react";
import { api, API, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UploadSimple, FilePdf, MagicWand, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATES = ["AN","AP","AR","AS","BR","CH","CG","DN","DD","DL","GA","GJ","HR","HP","JK","JH","KA","KL","LD","MP","MH","MN","ML","MZ","NL","OR","PY","PB","RJ","SK","TN","TG","TR","UP","UK","WB"];
const GST_RATES = [0, 5, 12, 18, 28];

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="label-caps mb-2 block">{label}</Label>
      {children}
      {hint && <div className="text-[10.5px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

const empty = {
  invoice_number: "",
  client_name: "",
  client_gstin: "",
  client_state: "",
  project_id: "",
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  subtotal: "",
  gst_rate: 18,
  cgst_amount: "",
  sgst_amount: "",
  igst_amount: "",
  total: "",
  notes: "",
};

/**
 * Slide-in sheet to upload a Tally-generated invoice PDF along with its metadata.
 * The PDF is stored via the existing object-storage attachment pipeline and the
 * invoice record is persisted with source='tally'.
 */
export function InvoiceUploadSheet({ projects, company, onUploaded, open: openProp, onOpenChange, hideTrigger }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = onOpenChange || setOpenInternal;
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseErr, setParseErr] = useState("");

  const homeState = (company?.state || "").toUpperCase();
  const clientStateUp = (form.client_state || "").toUpperCase();
  const isInterstate = homeState && clientStateUp && homeState !== clientStateUp;

  // Auto-compute CGST/SGST/IGST and total when subtotal + GST rate are set
  // (user can override any field explicitly)
  const preview = useMemo(() => {
    const s = Number(form.subtotal || 0);
    const rate = Number(form.gst_rate || 0);
    const tax = (s * rate) / 100;
    return {
      cgst: isInterstate ? 0 : tax / 2,
      sgst: isInterstate ? 0 : tax / 2,
      igst: isInterstate ? tax : 0,
      total: s + tax,
    };
  }, [form.subtotal, form.gst_rate, isInterstate]);

  const reset = () => { setForm(empty); setFile(null); setParseErr(""); };

  /**
   * Send the picked file to the backend LLM parser and merge the returned
   * fields into `form`. Existing user edits are preserved (only empty fields
   * get overwritten). If the LLM fails, we surface the error but keep the
   * file selected — the user can still fill fields manually.
   */
  const parsePdf = useCallback(async (chosen) => {
    if (!chosen) return;
    setParsing(true); setParseErr("");
    try {
      const fd = new FormData();
      fd.append("file", chosen);
      const { data } = await api.post("/invoices/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Merge: only overwrite fields the user hasn't already typed into
      setForm((prev) => {
        const next = { ...prev };
        const merge = (k, v) => {
          if (v === undefined || v === null || v === "") return;
          const cur = prev[k];
          const curEmpty = cur === "" || cur === null || cur === undefined || cur === 0;
          if (curEmpty || cur === empty[k]) next[k] = v;
        };
        merge("invoice_number", data.invoice_number);
        merge("client_name", data.client_name);
        merge("client_gstin", data.client_gstin);
        merge("client_state", data.client_state);
        if (data.issue_date) merge("issue_date", data.issue_date);
        merge("due_date", data.due_date);
        merge("subtotal", data.subtotal ? String(data.subtotal) : "");
        merge("cgst_amount", data.cgst_amount ? String(data.cgst_amount) : "");
        merge("sgst_amount", data.sgst_amount ? String(data.sgst_amount) : "");
        merge("igst_amount", data.igst_amount ? String(data.igst_amount) : "");
        merge("total", data.total ? String(data.total) : "");
        merge("notes", data.notes);
        return next;
      });
      toast.success("Details extracted — please review before saving");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setParseErr(msg);
      toast.warning(`Auto-fill failed: ${msg}. Please enter details manually.`);
    } finally {
      setParsing(false);
    }
  }, []);

  const onFilePicked = (e) => {
    const chosen = e.target.files?.[0] || null;
    setFile(chosen);
    setParseErr("");
    if (chosen) parsePdf(chosen);
  };

  const submit = async () => {
    if (!file) { toast.error("Please attach the Tally invoice PDF"); return; }
    if (!form.invoice_number || !form.client_name || !form.issue_date || !form.due_date) {
      toast.error("Invoice #, client, issue date and due date are required");
      return;
    }
    // Final values (use manual override if user typed, else preview)
    const cgst = form.cgst_amount !== "" ? Number(form.cgst_amount) : preview.cgst;
    const sgst = form.sgst_amount !== "" ? Number(form.sgst_amount) : preview.sgst;
    const igst = form.igst_amount !== "" ? Number(form.igst_amount) : preview.igst;
    const subtotal = Number(form.subtotal || 0);
    const total = form.total !== "" ? Number(form.total) : preview.total;
    if (total <= 0) { toast.error("Invoice total must be greater than zero"); return; }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("invoice_number", form.invoice_number);
    fd.append("client_name", form.client_name);
    fd.append("issue_date", form.issue_date);
    fd.append("due_date", form.due_date);
    fd.append("total", String(total));
    fd.append("subtotal", String(subtotal));
    fd.append("cgst_amount", String(cgst));
    fd.append("sgst_amount", String(sgst));
    fd.append("igst_amount", String(igst));
    fd.append("client_gstin", form.client_gstin || "");
    fd.append("client_state", form.client_state || "");
    fd.append("place_of_supply", form.client_state || "");
    if (form.project_id) fd.append("project_id", form.project_id);
    if (form.notes) fd.append("notes", form.notes);

    setBusy(true);
    try {
      await api.post("/invoices/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Tally invoice uploaded");
      setOpen(false); reset();
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      {!hideTrigger && (
        <SheetTrigger asChild>
          <Button data-testid="upload-invoice-button" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
            <UploadSimple size={16} className="mr-2" /> Upload Invoice
          </Button>
        </SheetTrigger>
      )}
      <SheetContent className="w-[520px] sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display">Upload Tally Invoice</SheetTitle>
        </SheetHeader>
        <div className="text-[11.5px] text-slate-500 mt-1 mb-5">
          Attach the PDF generated by Tally and record its details for AR tracking.
        </div>

        <div className="space-y-4">
          <Field
            label={
              <span className="flex items-center gap-2">
                Tally PDF
                <span className="inline-flex items-center gap-1 text-[9px] tracking-widest font-semibold uppercase bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                  <MagicWand size={10} /> AI auto-fill
                </span>
              </span>
            }
            hint="PDF, JPG or PNG · max 20 MB. Selecting a file will auto-extract details."
          >
            <label
              htmlFor="tally-pdf-input"
              className="flex items-center gap-3 border border-dashed border-slate-300 hover:border-[#EA580C] rounded-sm p-3 cursor-pointer bg-slate-50"
            >
              {parsing ? (
                <CircleNotch size={22} className="text-[#EA580C] animate-spin" />
              ) : (
                <FilePdf size={22} className="text-[#EA580C]" />
              )}
              <div className="flex-1 min-w-0">
                {file ? (
                  <>
                    <div className="text-sm font-medium truncate">{file.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                      {parsing && <span className="text-[#EA580C] ml-2">· Extracting details…</span>}
                      {!parsing && !parseErr && form.invoice_number && (
                        <span className="text-emerald-700 ml-2">· Auto-filled ✓</span>
                      )}
                      {!parsing && parseErr && (
                        <span className="text-red-600 ml-2">· Auto-fill failed — enter manually</span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-medium text-slate-700">Click to choose file</div>
                    <div className="text-[11px] text-slate-500">Details will be auto-extracted with AI</div>
                  </>
                )}
              </div>
              {file && !parsing && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); parsePdf(file); }}
                  data-testid="reparse-pdf"
                  className="text-[10.5px] uppercase tracking-widest font-semibold text-[#EA580C] hover:text-[#c2410c] px-2 py-1 border border-[#EA580C] rounded-sm"
                >
                  Re-parse
                </button>
              )}
              <input
                id="tally-pdf-input"
                data-testid="tally-pdf-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={onFilePicked}
                className="hidden"
              />
            </label>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Invoice # (from Tally)">
              <Input
                data-testid="upload-invoice-number"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                placeholder="e.g. TL/2026/00123"
              />
            </Field>
            <Field label="Project (optional)">
              <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="upload-invoice-project"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Client name">
            <Input
              data-testid="upload-client-name"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Client GSTIN">
              <Input
                placeholder="22AAAAA0000A1Z5"
                value={form.client_gstin}
                onChange={(e) => setForm({ ...form, client_gstin: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Client state">
              <Select value={form.client_state || "__none__"} onValueChange={(v) => setForm({ ...form, client_state: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="__none__">—</SelectItem>
                  {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="text-xs bg-slate-50 border border-slate-200 px-3 py-2 rounded-sm">
            <span className="label-caps mr-2">GST Type</span>
            <span className="font-mono-num font-semibold" data-testid="upload-gst-type">
              {homeState && clientStateUp
                ? (isInterstate ? `IGST (Inter-state · ${homeState} → ${clientStateUp})` : `CGST + SGST (Intra-state · ${homeState})`)
                : "— (set company & client state)"}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Issue date">
              <Input type="date" data-testid="upload-issue-date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
            </Field>
            <Field label="Due date">
              <Input type="date" data-testid="upload-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Subtotal (₹)">
              <Input
                type="number" step="0.01"
                data-testid="upload-subtotal"
                value={form.subtotal}
                onChange={(e) => setForm({ ...form, subtotal: e.target.value })}
                placeholder="e.g. 50000"
              />
            </Field>
            <Field label="GST rate">
              <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1 text-sm font-mono-num">
            <div className="flex justify-between text-slate-500 text-[11px]"><span>Computed from subtotal × GST · override below if Tally differs</span><span></span></div>
            <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(Number(form.subtotal || 0))}</span></div>
            {isInterstate ? (
              <div className="flex justify-between text-slate-700"><span>IGST (computed)</span><span>{fmtMoney(preview.igst)}</span></div>
            ) : (
              <>
                <div className="flex justify-between text-slate-700"><span>CGST (computed)</span><span>{fmtMoney(preview.cgst)}</span></div>
                <div className="flex justify-between text-slate-700"><span>SGST (computed)</span><span>{fmtMoney(preview.sgst)}</span></div>
              </>
            )}
            <div className="flex justify-between font-bold text-[#EA580C] text-base pt-1 border-t border-slate-200">
              <span>Total (computed)</span>
              <span data-testid="upload-total-preview">{fmtMoney(preview.total)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="CGST override (opt.)">
              <Input type="number" step="0.01" value={form.cgst_amount} onChange={(e) => setForm({ ...form, cgst_amount: e.target.value })} placeholder="auto" />
            </Field>
            <Field label="SGST override (opt.)">
              <Input type="number" step="0.01" value={form.sgst_amount} onChange={(e) => setForm({ ...form, sgst_amount: e.target.value })} placeholder="auto" />
            </Field>
            <Field label="IGST override (opt.)">
              <Input type="number" step="0.01" value={form.igst_amount} onChange={(e) => setForm({ ...form, igst_amount: e.target.value })} placeholder="auto" />
            </Field>
            <Field label="Grand total override">
              <Input
                type="number" step="0.01"
                data-testid="upload-total-override"
                value={form.total}
                onChange={(e) => setForm({ ...form, total: e.target.value })}
                placeholder="auto"
              />
            </Field>
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          <Button
            onClick={submit}
            disabled={busy}
            data-testid="save-uploaded-invoice"
            className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800"
          >
            {busy ? "Uploading…" : "Upload Invoice"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Opens the Tally-uploaded PDF in a new tab. */
export function openTallyPdf(inv) {
  if (!inv?.attachment_id) return;
  const url = `${API}/attachments/${inv.attachment_id}/download`;
  window.open(url, "_blank", "noopener,noreferrer");
}
